import React, { useState, useEffect, useRef } from "react";
import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDoc,
    Timestamp,
    query,
    orderBy,
    where
} from "firebase/firestore";
import { db } from "../firebase";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import CustomerMaster from "./CustomerMaster";
import ItemMaster from "./ItemMaster";
import { INDIAN_STATES, getStateWithCode, STATE_TO_CODE, getBaseState } from "../utils/constants";
import Loader from "../components/Loader";

const INV_COL = "INVOICES";
const QTN_COL = "QUOTATIONS";
const PROF_COL = "PROFORMA_INVOICES";
const DC_COL = "CHALANS";
const CUST_COL = "CUSTOMER_MASTER";
const ITEM_COL = "ITEM_MASTER";
const PROFILE_COL = "COMPANY_PROFILE";
const PROFILE_ID = "main_profile";

export default function Invoices() {
    const [view, setView] = useState("OVERVIEW"); // OVERVIEW, INVOICE, QUOTATION, CHALAN, CUSTOMERS, ITEMS
    const [docs, setDocs] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [itemsMaster, setItemsMaster] = useState([]);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showQuickCust, setShowQuickCust] = useState(false);
    const [showQuickItem, setShowQuickItem] = useState(false);
    const [activeItemIndex, setActiveItemIndex] = useState(null);
    const [counts, setCounts] = useState({
        INVOICE: 0,
        QUOTATION: 0,
        PROFORMA: 0,
        CHALAN: 0,
        CUSTOMERS: 0,
        ITEMS: 0
    });
    const [summary, setSummary] = useState({
        totalSales: 0,
        totalCollections: 0,
        pendingAmount: 0,
        totalQuotes: 0,
        collectionRate: 0,
        topCustomers: []
    });
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [recentQuotations, setRecentQuotations] = useState([]);
    const [overviewMonth, setOverviewMonth] = useState(new Date().getMonth() + 1); // 1-12
    const [overviewYear, setOverviewYear] = useState(new Date().getFullYear());
    const [allTimeOverview, setAllTimeOverview] = useState(false);
    const [allDocsCache, setAllDocsCache] = useState({ invoices: [], quotes: [] });
    const [summaryLoading, setSummaryLoading] = useState(false);

    const customerRef = useRef();
    const itemRef = useRef();

    // Quick Add States (Now with full details)
    const [quickCust, setQuickCust] = useState({
        name: "",
        phone: "",
        gstin: "",
        state: "Tamil Nadu (33)",
        address: "",
        email: "",
        shippingAddress: "",
        shippingState: "Tamil Nadu (33)",
        sameAsBilling: true
    });
    const [quickItem, setQuickItem] = useState({
        name: "",
        type: "Goods",
        hsnSac: "",
        partNumber: "",
        description: "",
        purchasePrice: "",
        sellingPrice: "",
        defaultRate: "",
        taxRate: 18,
        unit: "Nos"
    });

    const [editingId, setEditingId] = useState(null);
    const [isViewOnly, setIsViewOnly] = useState(false);

    // Filter & Pagination State
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    // Form State
    const [formData, setFormData] = useState({
        docNumber: "",
        date: new Date().toISOString().split('T')[0],
        poNumber: "",
        customerId: "",
        items: [{ itemId: "", description: "", itemNote: "", hsnSac: "", qty: 1, rate: 0, discountType: "₹", discount: 0, taxRate: 18, amount: 0, taxAmount: 0, unit: "Nos" }],
        remarks: "",
        paymentTerms: "Due on receipt",
        status: "DRAFT",
        challanType: "NON_RETURNABLE",
        returnRemarks: ""
    });

    useEffect(() => {
        if (["INVOICE", "QUOTATION", "PROFORMA", "CHALAN"].includes(view)) {
            fetchDocs();
        }
        fetchMasters();
    }, [view]);

    const fetchDocs = async () => {
        setLoading(true);
        try {
            const col = view === "INVOICE" ? INV_COL : (view === "QUOTATION" ? QTN_COL : (view === "PROFORMA" ? PROF_COL : DC_COL));
            const q = query(collection(db, col), orderBy("date", "desc"));
            const snap = await getDocs(q);
            setDocs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Error fetching docs:", err);
        } finally {
            setLoading(false);
        }
    };

    const generateDocNumber = (pData = profile, currentView = view) => {
        if (!pData) return "";
        const prefix = currentView === "INVOICE" ? pData.invPrefix : (currentView === "QUOTATION" ? pData.qtnPrefix : (currentView === "PROFORMA" ? pData.profPrefix : pData.dcPrefix));
        const next = currentView === "INVOICE" ? pData.invNext : (currentView === "QUOTATION" ? pData.qtnNext : (currentView === "PROFORMA" ? pData.profNext : pData.dcNext));
        return `${prefix || ""}${String(next || 1).padStart(3, '0')} `;
    };

    const fetchMasters = async () => {
        setSummaryLoading(true);
        try {
            const cSnap = await getDocs(collection(db, CUST_COL));
            const cCount = cSnap.size;
            setCustomers(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            const iSnap = await getDocs(collection(db, ITEM_COL));
            const iCount = iSnap.size;
            setItemsMaster(iSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            const invSnap = await getDocs(collection(db, INV_COL));
            const qtnSnap = await getDocs(collection(db, QTN_COL));
            const profSnap = await getDocs(collection(db, PROF_COL));
            const dcSnap = await getDocs(collection(db, DC_COL));

            const invData = invSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const qtnData = qtnSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            setAllDocsCache({ invoices: invData, quotes: qtnData });
            updateSummaryData(invData, qtnData);

            setCounts({
                INVOICE: invSnap.size,
                QUOTATION: qtnSnap.size,
                PROFORMA: profSnap.size,
                CHALAN: dcSnap.size,
                CUSTOMERS: cCount,
                ITEMS: iCount
            });

            const pRef = doc(db, PROFILE_COL, PROFILE_ID);
            const pSnap = await getDoc(pRef);
            if (pSnap.exists()) {
                setProfile(pSnap.data());
            }
        } catch (err) {
            console.error("Error fetching masters:", err);
        } finally {
            setSummaryLoading(false);
            setLoading(false);
        }
    };

    const parseDate = (d) => {
        if (!d) return new Date();
        // Handle Firestore Timestamp
        if (d.toDate && typeof d.toDate === 'function') return d.toDate();
        if (d.seconds) return new Date(d.seconds * 1000);

        // Handle String dates
        const dateStr = String(d);
        // Try standard parsing
        const parsed = new Date(dateStr);
        if (!isNaN(parsed.getTime())) return parsed;

        // Try DD/MM/YYYY
        const parts = dateStr.split(/[-/]/);
        if (parts.length === 3) {
            // Check if it's DD/MM/YYYY or YYYY-MM-DD
            if (parts[0].length === 4) return new Date(parts[0], parts[1] - 1, parts[2]); // YYYY MM DD
            return new Date(parts[2], parts[1] - 1, parts[0]); // DD MM YYYY
        }
        return new Date();
    };

    const updateSummaryData = (invOverride = null, qtnOverride = null) => {
        const allInvoices = invOverride || allDocsCache.invoices;
        const allQuotes = qtnOverride || allDocsCache.quotes;

        // Don't return if they are empty, as we might need to show ₹0 for a new month
        // Just proceed, the totals will remain 0

        let tSales = 0;
        let tCollections = 0;
        let pAmount = 0;

        const filteredInvoices = allInvoices
            .filter(d => {
                if (allTimeOverview) return true;
                const dDate = parseDate(d.date);
                return (dDate.getMonth() + 1) === Number(overviewMonth) && dDate.getFullYear() === Number(overviewYear);
            })
            .sort((a, b) => parseDate(b.date) - parseDate(a.date));

        const filteredQuotes = allQuotes
            .filter(d => {
                if (allTimeOverview) return true;
                const dDate = parseDate(d.date);
                return (dDate.getMonth() + 1) === Number(overviewMonth) && dDate.getFullYear() === Number(overviewYear);
            })
            .sort((a, b) => parseDate(b.date) - parseDate(a.date));

        const customerBilling = {};

        filteredInvoices.forEach(data => {
            const total = Number(data.total) || 0;
            if (data.status !== "CANCELLED") {
                tSales += total;
                if (data.status === "PAID") {
                    tCollections += total;
                } else {
                    pAmount += total;
                }

                // Group for Top Customers
                const cName = data.customerDetails?.name || "Unknown";
                customerBilling[cName] = (customerBilling[cName] || 0) + total;
            }
        });

        const collectionRate = tSales > 0 ? Math.round((tCollections / tSales) * 100) : 0;
        const topCustArray = Object.entries(customerBilling)
            .map(([name, total]) => ({ name, total }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 5);

        setSummary({
            totalSales: tSales,
            totalCollections: tCollections,
            pendingAmount: pAmount,
            totalQuotes: filteredQuotes.reduce((acc, d) => acc + (Number(d.total) || 0), 0),
            collectionRate,
            topCustomers: topCustArray
        });

        setRecentInvoices(filteredInvoices.slice(0, 5));
        setRecentQuotations(filteredQuotes.slice(0, 5));
    };

    useEffect(() => {
        // Only update local summary data without triggering the full 'summaryLoading' state 
        // if it's just a filter change (non-Firebase fetch)
        updateSummaryData();
    }, [allDocsCache, overviewMonth, overviewYear, allTimeOverview]);

    const handleAddItem = () => {
        setFormData({
            ...formData,
            items: [...formData.items, { itemId: "", description: "", itemNote: "", hsnSac: "", qty: 1, rate: 0, discountType: "₹", discount: 0, taxRate: 18, amount: 0, taxAmount: 0, unit: "Nos" }]
        });
    };

    const handleRemoveItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData({ ...formData, items: newItems });
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        if (field === "itemId") {
            const selectedItem = itemsMaster.find(i => i.id === value);
            if (selectedItem) {
                const amount = (selectedItem.defaultRate || 0) * newItems[index].qty;
                const taxRate = selectedItem.taxRate || 18;
                const taxAmount = (amount * taxRate) / 100;
                newItems[index] = {
                    ...newItems[index],
                    itemId: value,
                    description: selectedItem.name,
                    itemNote: selectedItem.description || "",
                    hsnSac: selectedItem.hsnSac,
                    rate: selectedItem.defaultRate || 0,
                    taxRate: taxRate,
                    unit: selectedItem.unit || "Nos",
                    amount: amount,
                    taxAmount: taxAmount
                };
            }
        } else {
            newItems[index][field] = value;
            if (field === "qty" || field === "rate" || field === "taxRate" || field === "discount" || field === "discountType") {
                const qty = Number(newItems[index].qty) || 0;
                const rate = Number(newItems[index].rate) || 0;
                let discountValue = 0;
                const dAmt = Number(newItems[index].discount) || 0;
                if (newItems[index].discountType === "%") {
                    discountValue = (qty * rate * dAmt) / 100;
                } else {
                    discountValue = dAmt;
                }
                const amount = Math.max(0, (qty * rate) - discountValue);
                newItems[index].amount = amount;
                newItems[index].taxAmount = (amount * newItems[index].taxRate) / 100;
            }
        }
        setFormData({ ...formData, items: newItems });
    };

    const calculateTotals = (items = formData.items, customerId = formData.customerId) => {
        let subtotal = 0;
        let totalTax = 0;
        const taxBreakdown = {};

        // Detect inter-state
        const companyState = profile?.state || "Tamil Nadu (33)";
        const selectedCustomer = customers.find(c => c.id === customerId);
        const customerStateFull = selectedCustomer?.state || "Tamil Nadu (33)";
        const hasGst = selectedCustomer?.gstin && selectedCustomer.gstin.trim() !== "";

        const cBase = getBaseState(customerStateFull);
        const pBase = getBaseState(companyState);
        const isInterState = cBase !== pBase;

        items.forEach(item => {
            const amt = Number(item.amount) || 0;
            const tRate = hasGst ? (Number(item.taxRate) || 0) : 0;
            const tAmt = hasGst ? (amt * tRate) / 100 : 0;

            subtotal += amt;
            totalTax += tAmt;

            if (!taxBreakdown[tRate]) {
                taxBreakdown[tRate] = { taxRate: tRate, taxableAmount: 0, taxAmount: 0 };
            }
            taxBreakdown[tRate].taxableAmount += amt;
            taxBreakdown[tRate].taxAmount += tAmt;
        });

        const total = subtotal + totalTax;
        return { subtotal, taxAmount: totalTax, total, taxBreakdown, isInterState };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.customerId || formData.items.length === 0) return alert("Customer and Items are required");

        setLoading(true);
        try {
            const totals = calculateTotals(formData.items, formData.customerId);
            const customer = customers.find(c => c.id === formData.customerId);

            const docData = {
                ...formData,
                customerDetails: customer,
                ...totals,
                isInterState: totals.isInterState,
                createdAt: Timestamp.now(),
                date: Timestamp.fromDate(new Date(formData.date))
            };

            const col = view === "INVOICE" ? INV_COL : (view === "QUOTATION" ? QTN_COL : (view === "PROFORMA" ? PROF_COL : DC_COL));

            if (editingId) {
                await updateDoc(doc(db, col, editingId), docData);
                alert(`${view} updated successfully`);
            } else {
                await addDoc(collection(db, col), docData);

                // Increment sequence in Profile
                if (profile) {
                    const profileRef = doc(db, PROFILE_COL, PROFILE_ID);
                    const seqField = view === "INVOICE" ? "invNext" : (view === "QUOTATION" ? "qtnNext" : (view === "PROFORMA" ? "profNext" : "dcNext"));
                    await updateDoc(profileRef, {
                        [seqField]: (profile[seqField] || 1) + 1
                    });
                }
                alert(`${view} created successfully`);
            }

            setShowModal(false);
            setEditingId(null);
            fetchDocs();
        } catch (err) {
            console.error("Error saving doc:", err);
            alert("Failed to save document");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this document?")) return;
        setLoading(true);
        try {
            const col = view === "INVOICE" ? INV_COL : (view === "QUOTATION" ? QTN_COL : (view === "PROFORMA" ? PROF_COL : DC_COL));
            await deleteDoc(doc(db, col, id));
            fetchDocs();
        } catch (err) {
            console.error("Error deleting doc:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        setLoading(true);
        try {
            const col = view === "INVOICE" ? INV_COL : (view === "QUOTATION" ? QTN_COL : (view === "PROFORMA" ? PROF_COL : DC_COL));
            await updateDoc(doc(db, col, id), { status: newStatus });
            await fetchDocs();
        } catch (err) {
            console.error("Error updating status:", err);
            alert("Failed to update status");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (doc) => {
        setIsViewOnly(false);
        setFormData({
            ...doc,
            date: doc.date instanceof Timestamp ? doc.date.toDate().toISOString().split('T')[0] : doc.date,
            paymentTerms: doc.paymentTerms || "Due on receipt",
            remarks: doc.remarks || "",
            poNumber: doc.poNumber || "",
            challanType: doc.challanType || "NON_RETURNABLE",
            returnRemarks: doc.returnRemarks || ""
        });
        setEditingId(doc.id);
        setShowModal(true);
    };

    const handleView = (doc) => {
        setIsViewOnly(true);
        setFormData({
            ...doc,
            date: doc.date instanceof Timestamp ? doc.date.toDate().toISOString().split('T')[0] : doc.date,
            paymentTerms: doc.paymentTerms || "Due on receipt",
            remarks: doc.remarks || "",
            poNumber: doc.poNumber || "",
            challanType: doc.challanType || "NON_RETURNABLE",
            returnRemarks: doc.returnRemarks || ""
        });
        setEditingId(doc.id);
        setShowModal(true);
    };

    const handleExportExcel = () => {
        const docLabel = view === "INVOICE" ? "Invoice" : (view === "QUOTATION" ? "Quotation" : (view === "PROFORMA" ? "Proforma" : "DC"));
        const data = docs.map(doc => {
            const isInter = doc.isInterState;
            return {
                [`${docLabel} Number`]: doc.docNumber,
                "Date": doc.date instanceof Timestamp ? doc.date.toDate().toLocaleDateString() : doc.date,
                "Customer": doc.customerDetails?.name || "N/A",
                "Customer GSTIN": doc.customerDetails?.gstin || "N/A",
                "Taxable Amount": doc.subtotal.toFixed(2),
                "CGST": !isInter ? (doc.taxAmount / 2).toFixed(2) : "0.00",
                "SGST": !isInter ? (doc.taxAmount / 2).toFixed(2) : "0.00",
                "IGST": isInter ? (doc.taxAmount.toFixed(2)) : "0.00",
                "Total Amount": doc.total.toFixed(2),
                "Status": doc.status || "DRAFT"
            };
        });

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Documents");
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, `${view}_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleDownloadGSTJson = () => {
        // Only invoices should be filing GST
        const invoices = filteredDocs.filter(d => (d.type || view) === "INVOICE");

        const b2b = invoices.reduce((acc, inv) => {
            const gstin = inv.customerDetails?.gstin || "URP"; // URP for unregistered persons if needed, but B2B requires GSTIN
            let customerEntry = acc.find(c => c.ctin === gstin);

            if (!customerEntry) {
                customerEntry = { ctin: gstin, inv: [] };
                acc.push(customerEntry);
            }

            const date = inv.date instanceof Timestamp ? inv.date.toDate() : new Date(inv.date);
            const formattedDate = `${date.getDate().toString().padStart(2, '0')} -${(date.getMonth() + 1).toString().padStart(2, '0')} -${date.getFullYear()} `;

            const customerStateStr = inv.customerDetails?.state || "Tamil Nadu";
            let posCode = "33";
            const stateMatch = Object.entries(STATE_TO_CODE).find(([name]) => customerStateStr.startsWith(name));
            if (stateMatch) posCode = stateMatch[1];

            const groupedItems = inv.items.reduce((gi, item) => {
                let rateEntry = gi.find(r => r.rt === item.taxRate);
                if (!rateEntry) {
                    rateEntry = { rt: item.taxRate, txval: 0, camt: 0, samt: 0, iamt: 0 };
                    gi.push(rateEntry);
                }
                rateEntry.txval += item.amount;
                // Assuming intra-state for simplicity, or we check state later
                const baseCompanyState = getBaseState(profile?.state || "Tamil Nadu");
                const customerState = inv.customerDetails?.state || "Tamil Nadu";
                const isInterState = getBaseState(customerState) !== baseCompanyState;

                if (isInterState) {
                    rateEntry.iamt += item.taxAmount;
                } else {
                    rateEntry.camt += item.taxAmount / 2;
                    rateEntry.samt += item.taxAmount / 2;
                }
                return gi;
            }, []);

            customerEntry.inv.push({
                inum: inv.docNumber,
                idt: formattedDate,
                val: inv.total,
                pos: posCode,
                rchrg: "N",
                itms: groupedItems.map((itm, idx) => ({
                    num: idx + 1,
                    itm_det: {
                        rt: itm.rt,
                        txval: Number(itm.txval.toFixed(2)),
                        camt: Number(itm.camt.toFixed(2)),
                        samt: Number(itm.samt.toFixed(2)),
                        iamt: Number(itm.iamt.toFixed(2))
                    }
                }))
            });

            return acc;
        }, []);

        const finalJson = { b2b };
        const blob = new Blob([JSON.stringify(finalJson, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `GSTR1_Export_${new Date().toISOString().split('T')[0]}.json`;
        link.click();
    };


    const handlePrint = (docData) => {
        const printWindow = window.open("", "_blank");
        const company = profile || { companyName: "ASIAN CLIMATE CONTROLS", address: "Kanchipuram, Tamil Nadu", phone: "", email: "", gstin: "", bankName: "", accNumber: "", ifscCode: "", state: "Tamil Nadu (33)" };

        const baseCompanyState = getBaseState(company.state || "Tamil Nadu (33)");
        const isInterState = getBaseState(docData.customerDetails?.state || "Tamil Nadu (33)") !== baseCompanyState;
        const hasGst = docData.customerDetails?.gstin && docData.customerDetails.gstin.trim() !== "";

        // Ensure taxBreakdown exists (re-calculate from docData.items)
        const totals = calculateTotals(docData.items, docData.customerId);
        const taxBreakdown = totals.taxBreakdown;

        const docNum = docData.docNumber || "N/A";
        const docType = docData.type || docData.view || view;

        // Determine the document title based on type
        let docTitle = docType === "CHALAN" ? "DELIVERY CHALAN" : (docType === "QUOTATION" ? "QUOTATION" : (docType === "PROFORMA" ? "PROFORMA INVOICE" : `TAX ${docType}`));
        if (docType === "CHALAN") {
            docTitle = (docData.challanType || "NON_RETURNABLE") === "RETURNABLE" ? "RETURNABLE DELIVERY CHALAN" : "NON-RETURNABLE DELIVERY CHALAN";
        }

        const amountInWords = (num) => {
            const a = ['', 'one ', 'two ', 'three ', 'four ', 'five ', 'six ', 'seven ', 'eight ', 'nine ', 'ten ', 'eleven ', 'twelve ', 'thirteen ', 'fourteen ', 'fifteen ', 'sixteen ', 'seventeen ', 'eighteen ', 'nineteen '];
            const b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
            const inWords = (n) => {
                if ((n = n.toString()).length > 9) return 'overflow';
                const n_array = ('000000000' + n).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
                if (!n_array) return '';
                let str = '';
                str += (Number(n_array[1]) !== 0) ? (a[Number(n_array[1])] || b[n_array[1][0]] + ' ' + a[n_array[1][1]]) + 'crore ' : '';
                str += (Number(n_array[2]) !== 0) ? (a[Number(n_array[2])] || b[n_array[2][0]] + ' ' + a[n_array[2][1]]) + 'lakh ' : '';
                str += (Number(n_array[3]) !== 0) ? (a[Number(n_array[3])] || b[n_array[3][0]] + ' ' + a[n_array[3][1]]) + 'thousand ' : '';
                str += (Number(n_array[4]) !== 0) ? (a[Number(n_array[4])] || b[n_array[4][0]] + ' ' + a[n_array[4][1]]) + 'hundred ' : '';
                str += (Number(n_array[5]) !== 0) ? (a[Number(n_array[5])] || b[n_array[5][0]] + ' ' + a[n_array[5][1]]) + ' ' : '';
                return str;
            }
            const [whole, decimal] = Number(num).toFixed(2).split('.');
            let res = inWords(whole);
            if (res) res = res.charAt(0).toUpperCase() + res.slice(1) + 'Rupees ';
            if (Number(decimal) > 0) res += 'and ' + inWords(decimal) + 'Paise ';
            return res + 'Only';
        };

        const html = `
<!DOCTYPE html>
<html>
    <head>
      <title>${docTitle} - ${docData.docNumber}</title>
      <style>
        body { font-family: 'Segoe UI', 'Calibri', 'Arial', sans-serif; padding: 10px; color: #000; font-size: 11px; line-height: 1.3; }
        .invoice-box { max-width: 900px; margin: auto; padding: 10px; border: 1px solid #000; }
        .header-table { width: 100%; border-collapse: collapse; border-bottom: 2px solid #000; margin-bottom: 10px; }
        .header-table td { border: none !important; padding: 5px; }
        .company-brand h1 { margin: 0; font-size: 24px; font-weight: bold; text-transform: uppercase; font-family: 'Segoe UI', 'Calibri', 'Arial', sans-serif; letter-spacing: 1px; }
        .company-details { font-size: 11px; margin-top: 5px; line-height: 1.5; }
        .doc-type-header { text-align: right; }
        .doc-type-header h2 { margin: 0; font-size: 16px; text-transform: uppercase; color: #333; }
        .info-grid { display: flex; border-bottom: 1px solid #000; margin-bottom: 10px; border-top: 1px solid #000; }
        .info-box { flex: 1; padding: 8px; border-right: 1px solid #000; }
        .info-box:last-child { border-right: none; }
        .box-title { font-weight: bold; font-size: 9px; color: #555; text-decoration: underline; margin-bottom: 4px; }
        .invoice-meta { width: 100%; border-collapse: collapse; }
        .invoice-meta td { padding: 1px 0; border: none !important; white-space: nowrap; }
        .invoice-meta td:first-child { font-weight: bold; width: 90px; }
        .main-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
        .main-table th { background: #eee; color: #000; text-transform: uppercase; font-size: 9px; padding: 5px 3px; border: 1px solid #000; }
        .main-table td { border: 1px solid #000; padding: 5px 3px; vertical-align: top; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .summary-section { display: flex; border-top: 0px solid #000; }
        .summary-left { flex: 1.4; padding: 8px; border-right: 1px solid #000; border-top: 1px solid #000; border-bottom: 1px solid #000; }
        .summary-right { flex: 1; padding: 0; border-top: 1px solid #000; border-bottom: 1px solid #000; }
        .bank-box { border: 1px solid #ccc; padding: 6px; margin-top: 5px; background: #fff; font-size: 9px; }
        .totals-table { width: 100%; border-collapse: collapse; }
        .totals-table td { padding: 4px 6px; border-bottom: 1px solid #eee; }
        .totals-table td:last-child { text-align: right; font-weight: bold; width: 90px; }
        .grand-total-row { background: #eee; font-weight: bold; font-size: 12px; border-top: 1px solid #000; }
        .tax-breakdown-table { width: 100%; border-collapse: collapse; margin-top: 5px; font-size: 9px; }
        .tax-breakdown-table th, .tax-breakdown-table td { border: 1px solid #ddd; padding: 3px; text-align: center; }
        .footer-notes { margin-top: 8px; font-size: 9px; }
        .signature-area { margin-top: 25px; display: flex; justify-content: flex-end; }
        .sig-box { text-align: center; width: 180px; font-size: 10px; }
        strong { font-size: 13px; }
        .info-box strong { font-size: 12px; }
        @media print { body { padding: 0; } .invoice-box { border: 1px solid #000; } @page { size: portrait; margin: 0.5cm; } }
      </style>
    </head>
    <body>
      <div class="invoice-box">
        <table class="header-table">
          <tr>
            <td width="55%">
              <div class="company-brand">
                <h1>${company.companyName}</h1>
                <div class="company-details">
                  ${company.address}, ${company.city}<br/>
                  ${company.state} - ${company.pincode}<br/>
                  ${company.phone ? 'Ph: ' + company.phone : ''} ${company.email ? ' | ' + company.email : ''}<br/>
                  <strong>GSTIN: ${company.gstin}</strong>
                </div>
              </div>
            </td>
            <td width="45%" style="vertical-align: top;">
              <div class="doc-type-header"><h2>${docTitle}</h2></div>
              <table class="invoice-meta">
                <tr><td>${docType === "INVOICE" ? "Invoice" : (docType === "QUOTATION" ? "Quotation" : "DC")} No</td><td>: ${docNum}</td></tr>
                <tr><td>Date</td><td>: ${docData.date?.toDate().toLocaleDateString()}</td></tr>
                <tr><td>P.O. Number</td><td>: ${docData.poNumber || "-"}</td></tr>
                <tr><td>Payment Terms</td><td>: ${docData.paymentTerms || "Due on receipt"}</td></tr>
                <tr><td>Place of Supply</td><td>: ${getStateWithCode(docData.customerDetails?.state)}</td></tr>
              </table>
            </td>
          </tr>
        </table>
        <div class="info-grid">
          <div class="info-box">
            <div class="box-title">BILL TO</div>
            <div class="box-content">
              <strong>${docData.customerDetails?.name}</strong><br/>
              ${docData.customerDetails?.address || 'N/A'}<br/>
              <strong>GSTIN: ${docData.customerDetails?.gstin || "N/A"}</strong><br/>
              Phone: ${docData.customerDetails?.phone}
            </div>
          </div>
          <div class="info-box" style="border-right: none;">
            <div class="box-title">SHIP TO</div>
            <div class="box-content">
              <strong>${docData.customerDetails?.name}</strong><br/>
              ${docData.customerDetails?.shippingAddress || docData.customerDetails?.address || 'N/A'}<br/>
              <strong>State Code:</strong> ${getStateWithCode(docData.customerDetails?.shippingState || docData.customerDetails?.state)}
            </div>
          </div>
        </div>
        <table class="main-table">
          <thead>
            <tr>
              <th width="4%">#</th>
              <th width="35%">Item & Description</th>
              <th width="10%">HSN/SAC</th>
              <th width="6%">Qty</th>
              <th width="10%">Rate</th>
              <th width="8%">Disc</th>
              <th width="8%">GST %</th>
              <th width="15%" class="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${docData.items.map((item, i) => `
              <tr>
                <td class="text-center">${i + 1}</td>
                <td>
                  <strong>${item.description}</strong><br/>
                  <span style="font-size: 10px; color: #444;">${item.itemNote || ""}</span>
                </td>
                <td class="text-center">${item.hsnSac || "-"}</td>
                <td class="text-center">${item.qty} ${item.unit || "Nos"}</td>
                <td class="text-right">${Number(item.rate).toFixed(2)}</td>
                <td class="text-right">${item.discount ? (item.discountType === "%" ? item.discount + "%" : "₹" + item.discount) : "-"}</td>
                <td class="text-center">${hasGst ? (item.taxRate || 0) : 0}%</td>
                <td class="text-right">${Number(item.amount).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="summary-section">
          <div class="summary-left">
            <p><strong>Amount in Words:</strong><br/>${amountInWords(docData.total)}</p>
            <div style="display: flex; gap: 15px; align-items: flex-start; margin-top: 5px;">
              <div class="bank-box" style="flex: 1; margin-top: 0; background: #fff;">
                <strong>BANK DETAILS:</strong> ${company.bankName} | A/C: ${company.accNumber} | IFSC: ${company.ifscCode}
                ${company.upiId ? `<br/><strong>UPI ID:</strong> ${company.upiId}` : ''}
              </div>
              ${company.upiId ? `
              <div style="text-align: center; border: 1px solid #ccc; padding: 4px; background: #fff;">
                <img src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(`upi://pay?pa=${company.upiId}&pn=${encodeURIComponent(company.companyName)}&am=${docData.total}&cu=INR`)}" alt="UPI QR" style="display: block; width: 80px; height: 80px;" />
                <div style="font-size: 8px; margin-top: 2px; font-weight: bold;">SCAN TO PAY</div>
              </div>
              ` : ''}
            </div>
            <table class="tax-breakdown-table">
                <thead>
                    <tr>
                        <th>GST %</th>
                        <th>Taxable Amt</th>
                        ${!isInterState ? '<th>CGST</th><th>SGST</th>' : '<th>IGST</th>'}
                        <th>Total Tax</th>
                    </tr>
                </thead>
                <tbody>
                    ${Object.values(taxBreakdown).map(t => `
                        <tr>
                            <td>${t.taxRate}%</td>
                            <td>${t.taxableAmount.toFixed(2)}</td>
                            ${!isInterState ? `
                                <td>${(t.taxAmount / 2).toFixed(2)}</td>
                                <td>${(t.taxAmount / 2).toFixed(2)}</td>
                            ` : `
                                <td>${t.taxAmount.toFixed(2)}</td>
                            `}
                            <td>${t.taxAmount.toFixed(2)}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
          </div>
          <div class="summary-right">
            <table class="totals-table">
              <tr><td>Sub Total</td><td>₹${Number(docData.subtotal).toFixed(2)}</td></tr>
              <tr><td>Total GST</td><td>₹${Number(docData.taxAmount).toFixed(2)}</td></tr>
              <tr class="grand-total-row"><td>Grand Total</td><td>₹${Number(docData.total).toFixed(2)}</td></tr>
            </table>
          </div>
        </div>
        <div class="footer-notes">
            <strong>Notes/Terms:</strong> ${docData.remarks || "Please pay within 7 days. Goods once sold will not be taken back."}
            ${docType === "CHALAN" && (docData.challanType || "NON_RETURNABLE") === "RETURNABLE" && docData.returnRemarks ? `
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px dashed #ccc;">
                <strong>Return Remarks:</strong> ${docData.returnRemarks}
            </div>` : ''}
        </div>
        <div class="signature-area">
          <div class="sig-box">
            For <strong>${company.companyName}</strong><br/><br/><br/>
            Authorized Signatory
          </div>
        </div>
      </div>
      <script>window.onload = function() { window.print(); window.close(); }</script>
    </body>
  </html>
    `;
        printWindow.document.write(html);
        printWindow.document.close();
    };

    const handleConvertToProforma = async (qtn) => {
        if (!window.confirm("Convert this Quotation to a Proforma Invoice?")) return;
        setLoading(true);
        try {
            const nextProfNumber = generateDocNumber(profile, "PROFORMA");
            const profData = {
                ...qtn,
                id: undefined, // remove qtn id
                type: "PROFORMA",
                view: "PROFORMA",
                docNumber: nextProfNumber,
                status: "DRAFT",
                createdAt: Timestamp.now(),
                convertedFrom: qtn.id
            };
            delete profData.id;

            await addDoc(collection(db, PROF_COL), profData);
            await updateDoc(doc(db, QTN_COL, qtn.id), { status: "CONVERTED" });

            if (profile) {
                await updateDoc(doc(db, PROFILE_COL, PROFILE_ID), {
                    profNext: (profile.profNext || 1) + 1
                });
            }

            alert("Quotation converted to Proforma Invoice successfully");
            setView("PROFORMA");
            fetchDocs();
        } catch (err) {
            console.error("Conversion failed:", err);
            alert("Failed to convert quotation");
        } finally {
            setLoading(false);
        }
    };

    const handleConvertToInvoice = async (prof) => {
        if (!window.confirm("Convert this Proforma to a Tax Invoice?")) return;
        setLoading(true);
        try {
            const nextInvNumber = generateDocNumber(profile, "INVOICE");
            const invoiceData = {
                ...prof,
                id: undefined, // remove prof id
                type: "INVOICE",
                view: "INVOICE",
                docNumber: nextInvNumber,
                status: "DRAFT",
                createdAt: Timestamp.now(),
                convertedFrom: prof.id
            };
            delete invoiceData.id;

            // Add the new invoice
            await addDoc(collection(db, INV_COL), invoiceData);

            // Update original proforma status
            await updateDoc(doc(db, PROF_COL, prof.id), { status: "CONVERTED" });

            // Increment sequence in Profile
            if (profile) {
                const profileRef = doc(db, PROFILE_COL, PROFILE_ID);
                await updateDoc(profileRef, {
                    invNext: (profile.invNext || 1) + 1
                });
            }

            alert("Proforma converted to Invoice successfully");
            setView("INVOICE");
            fetchDocs();
        } catch (err) {
            console.error("Conversion failed:", err);
            alert("Failed to convert quotation");
        } finally {
            setLoading(false);
        }
    };

    const handleQuickCustSave = async (e) => {
        e.preventDefault();
        if (!quickCust.name || !quickCust.phone) return alert("Name and Phone required");
        setLoading(true);
        try {
            const docRef = await addDoc(collection(db, CUST_COL), { ...quickCust, createdAt: Timestamp.now() });
            await fetchMasters();
            setFormData({ ...formData, customerId: docRef.id });
            setShowQuickCust(false);
            setQuickCust({ name: "", phone: "", gstin: "", state: "Tamil Nadu (33)", address: "", email: "", shippingAddress: "", shippingState: "Tamil Nadu (33)", sameAsBilling: true });
        } catch (err) { alert("Failed to add customer"); }
        finally { setLoading(false); }
    };

    const handleQuickItemSave = async (e) => {
        e.preventDefault();
        if (!quickItem.name || !quickItem.hsnSac) return alert("Name and HSN required");
        setLoading(true);
        try {
            const dataToSave = {
                ...quickItem,
                purchasePrice: Number(quickItem.purchasePrice || 0),
                sellingPrice: Number(quickItem.sellingPrice || 0),
                defaultRate: Number(quickItem.sellingPrice || quickItem.defaultRate || 0),
                createdAt: Timestamp.now()
            };

            const docRef = await addDoc(collection(db, ITEM_COL), dataToSave);

            // Sync with Part Stock if it's Goods
            if (dataToSave.type === "Goods") {
                const stockSnap = await getDocs(collection(db, "PART_STOCK"));
                const existingPart = stockSnap.docs.find(d => {
                    const p = d.data();
                    return p.name.toLowerCase() === dataToSave.name.toLowerCase() && p.partNumber === dataToSave.partNumber;
                });

                const partData = {
                    name: dataToSave.name,
                    partNumber: dataToSave.partNumber || "",
                    model: dataToSave.description || "",
                    purchasePrice: dataToSave.purchasePrice,
                    sellingPrice: dataToSave.sellingPrice,
                    hsnSac: dataToSave.hsnSac,
                    updatedAt: Timestamp.now()
                };

                if (existingPart) {
                    await updateDoc(doc(db, "PART_STOCK", existingPart.id), partData);
                } else {
                    await addDoc(collection(db, "PART_STOCK"), {
                        ...partData,
                        goodQty: 0,
                        defectiveQty: 0,
                        location: "GRPD",
                        remarks: "Created from Quick Add in Invoices",
                        createdAt: Timestamp.now()
                    });
                }
            }

            await fetchMasters();

            // Auto-fill the row that triggered the add
            if (activeItemIndex !== null) {
                handleItemChange(activeItemIndex, "itemId", docRef.id);
            }

            setShowQuickItem(false);
            setQuickItem({
                name: "",
                type: "Goods",
                hsnSac: "",
                partNumber: "",
                description: "",
                purchasePrice: "",
                sellingPrice: "",
                defaultRate: "",
                taxRate: 18,
                unit: "Nos"
            });
            setActiveItemIndex(null);
        } catch (err) {
            console.error("Error saving quick item:", err);
            alert("Failed to add item");
        } finally {
            setLoading(false);
        }
    };

    // Filtered & Paginated Logic
    const filteredDocs = docs.filter(d => {
        const docDate = d.date instanceof Timestamp ? d.date.toDate().toISOString().split('T')[0] : d.date;
        const matchesSearch = d.customerDetails?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            d.docNumber?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === "ALL" || d.status === statusFilter;

        const matchesStart = !startDate || docDate >= startDate;
        const matchesEnd = !endDate || docDate <= endDate;

        return matchesSearch && matchesStatus && matchesStart && matchesEnd;
    });

    const totalPages = Math.ceil(filteredDocs.length / itemsPerPage);
    const paginatedDocs = filteredDocs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const { subtotal, taxAmount, total, isInterState: isCurrentInterState } = calculateTotals(formData.items, formData.customerId);
    const currentCustomer = customers.find(c => c.id === formData.customerId);
    const hasGstInUI = currentCustomer?.gstin && currentCustomer.gstin.trim() !== "";

    return (
        <div className="container-fluid mt-3 px-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>Billing & Documents</h3>
                {["INVOICE", "QUOTATION", "PROFORMA", "CHALAN", "CUSTOMERS", "ITEMS"].includes(view) && (
                    <button className="btn btn-primary" onClick={() => {
                        if (view === "CUSTOMERS") {
                            customerRef.current?.openAddModal();
                        } else if (view === "ITEMS") {
                            itemRef.current?.openAddModal();
                        } else {
                            setFormData({
                                docNumber: generateDocNumber(),
                                date: new Date().toISOString().split('T')[0],
                                poNumber: "",
                                customerId: "",
                                items: [{ itemId: "", description: "", itemNote: "", hsnSac: "", qty: 1, rate: 0, taxRate: 18, amount: 0, taxAmount: 0, unit: "Nos" }],
                                remarks: "",
                                paymentTerms: "Due on receipt",
                                status: "DRAFT",
                                challanType: "NON_RETURNABLE",
                                returnRemarks: ""
                            });
                            setIsViewOnly(false);
                            setShowModal(true);
                        }
                    }}>
                        + {view === "CUSTOMERS" ? "Add New Customer" : (view === "ITEMS" ? "Add New Item" : `Create New ${view}`)}
                    </button>
                )}
            </div>

            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button
                        className={`nav-link ${view === "OVERVIEW" ? "active fw-bold" : ""}`}
                        onClick={() => setView("OVERVIEW")}
                    >
                        Overview
                    </button>
                </li>
                {["INVOICE", "QUOTATION", "PROFORMA", "CHALAN", "CUSTOMERS", "ITEMS"].map(v => (
                    <li className="nav-item" key={v}>
                        <button
                            className={`nav-link ${view === v ? "active fw-bold" : ""}`}
                            onClick={() => setView(v)}
                        >
                            {v === "CUSTOMERS" ? `Customer Master (${counts.CUSTOMERS})` :
                                v === "ITEMS" ? `Item Master (${counts.ITEMS})` :
                                    (v === "PROFORMA" ? `Proforma Invoices (${counts.PROFORMA})` : `${v.charAt(0) + v.slice(1).toLowerCase()}s (${counts[v]})`)}
                        </button>
                    </li>
                ))}
            </ul>

            {view === "CUSTOMERS" && <CustomerMaster ref={customerRef} />}
            {view === "ITEMS" && <ItemMaster ref={itemRef} />}

            {view === "OVERVIEW" && (
                <div className="py-2 text-start">
                    {/* Overview Filter Bar */}
                    <div className="card border-0 shadow-sm p-3 mb-4">
                        <div className="row align-items-center">
                            <div className="col-md-4">
                                <h5 className="fw-bold mb-0 text-muted">
                                    Financial Performance
                                    {!allTimeOverview && <span className="text-primary ms-2 opacity-75 small"> - {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][overviewMonth - 1]} {overviewYear}</span>}
                                    {allTimeOverview && <span className="text-secondary ms-2 opacity-75 small"> - All Time</span>}
                                </h5>
                            </div>
                            <div className="col-md-8 text-end">
                                <div className="d-flex gap-3 align-items-center justify-content-end">
                                    <div className="form-check form-switch mb-0">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={allTimeOverview}
                                            onChange={e => setAllTimeOverview(e.target.checked)}
                                            id="allTimeSwitch"
                                        />
                                        <label className="form-check-label small fw-bold" htmlFor="allTimeSwitch">Show All Time</label>
                                    </div>

                                    {!allTimeOverview && (
                                        <div className="d-flex gap-2">
                                            <select
                                                className="form-select form-select-sm border bg-light px-3"
                                                style={{ width: "150px" }}
                                                value={overviewMonth}
                                                onChange={e => setOverviewMonth(e.target.value)}
                                            >
                                                {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((m, i) => (
                                                    <option key={m} value={i + 1}>{m}</option>
                                                ))}
                                            </select>
                                            <select
                                                className="form-select form-select-sm border bg-light px-3"
                                                style={{ width: "100px" }}
                                                value={overviewYear}
                                                onChange={e => setOverviewYear(e.target.value)}
                                            >
                                                {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-4 mb-4">
                        <div className="col-md-9">
                            <div className="row g-4 h-100">
                                <div className="col-md-4">
                                    <div className="card p-4 border-0 bg-primary text-white shadow-sm h-100">
                                        {summaryLoading ? (
                                            <div className="d-flex align-items-center mb-2" style={{ height: '38px' }}>
                                                <div className="spinner-border spinner-border-sm text-white opacity-50" role="status"></div>
                                            </div>
                                        ) : (
                                            <h2 className="fw-bold">₹{summary.totalSales.toLocaleString()}</h2>
                                        )}
                                        <p className="fw-bold text-uppercase small opacity-75 mb-3">Total Invoiced</p>
                                        <div className="mt-auto">
                                            <div className="d-flex justify-content-between small opacity-75 mb-1">
                                                <span>Collection Rate</span>
                                                <span>{summaryLoading ? '...' : `${summary.collectionRate}%`}</span>
                                            </div>
                                            <div className="progress bg-white bg-opacity-25" style={{ height: '6px' }}>
                                                <div className="progress-bar bg-white" style={{ width: summaryLoading ? '0%' : `${summary.collectionRate}%`, transition: 'width 0.5s' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card p-4 border-0 bg-success text-white shadow-sm h-100">
                                        {summaryLoading ? (
                                            <div className="d-flex align-items-center mb-2" style={{ height: '38px' }}>
                                                <div className="spinner-border spinner-border-sm text-white opacity-50" role="status"></div>
                                            </div>
                                        ) : (
                                            <h2 className="fw-bold">₹{summary.totalCollections.toLocaleString()}</h2>
                                        )}
                                        <p className="fw-bold text-uppercase small opacity-75 mb-0">Total Collections</p>
                                        <i className="bi bi-cash-stack display-6 opacity-25 float-end"></i>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card p-4 border-0 bg-warning text-dark shadow-sm h-100">
                                        {summaryLoading ? (
                                            <div className="d-flex align-items-center mb-2" style={{ height: '38px' }}>
                                                <div className="spinner-border spinner-border-sm text-dark opacity-50" role="status"></div>
                                            </div>
                                        ) : (
                                            <h2 className="fw-bold">₹{summary.pendingAmount.toLocaleString()}</h2>
                                        )}
                                        <p className="fw-bold text-uppercase small opacity-75 mb-0">Pending Payment</p>
                                        <i className="bi bi-clock-history display-6 opacity-25 float-end"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card p-3 border-0 bg-info text-dark shadow-sm h-100">
                                {summaryLoading ? (
                                    <div className="d-flex align-items-center mb-2" style={{ height: '32px' }}>
                                        <div className="spinner-border spinner-border-sm text-info opacity-50" role="status"></div>
                                    </div>
                                ) : (
                                    <h3 className="fw-bold mb-1">₹{summary.totalQuotes.toLocaleString()}</h3>
                                )}
                                <p className="fw-bold text-uppercase x-small opacity-75 mb-0">Quotes</p>
                                <hr className="my-2 opacity-10" />
                                <div className="small fw-bold">Active Counts:</div>
                                <div className="d-flex justify-content-between small">
                                    <span>Quotations</span>
                                    <span>{counts.QUOTATION}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-4 mb-4">
                        <div className="col-md-8">
                            <div className="card border-0 shadow-sm">
                                <div className="card-header bg-white py-3">
                                    <h6 className="mb-0 fw-bold"><i className="bi bi-hourglass-split me-2"></i>Recent Invoiced Items</h6>
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0">
                                            <thead className="table-light">
                                                <tr className="small">
                                                    <th>Number</th>
                                                    <th>Customer</th>
                                                    <th>Date</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentInvoices.map(inv => (
                                                    <tr key={inv.id} className="small align-middle" onClick={() => handleEdit(inv)} style={{ cursor: "pointer" }}>
                                                        <td className="fw-bold text-primary">{inv.docNumber}</td>
                                                        <td>{inv.customerDetails?.name}</td>
                                                        <td>{parseDate(inv.date).toLocaleDateString()}</td>
                                                        <td className="fw-bold">₹{inv.total?.toLocaleString()}</td>
                                                        <td><span className={`badge ${inv.status === 'PAID' ? 'bg-success' : (inv.status === 'CANCELLED' ? 'bg-danger' : 'bg-warning')}`}>{inv.status}</span></td>
                                                    </tr>
                                                ))}
                                                {recentInvoices.length === 0 && <tr><td colSpan="5" className="text-center py-4 text-muted">No records for this period</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white py-3">
                                    <h6 className="mb-0 fw-bold text-success"><i className="bi bi-star-fill me-2"></i>Top Customers</h6>
                                </div>
                                <div className="card-body p-0">
                                    {summary.topCustomers.length > 0 ? (
                                        <div className="list-group list-group-flush">
                                            {summary.topCustomers.map((c, i) => (
                                                <div key={i} className="list-group-item d-flex justify-content-between align-items-center py-3">
                                                    <div>
                                                        <div className="fw-bold small">{c.name}</div>
                                                        <div className="x-small text-muted">{Math.round((c.total / summary.totalSales) * 100)}% of billings</div>
                                                    </div>
                                                    <span className="badge bg-light text-dark rounded-pill border">₹{c.total.toLocaleString()}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-5 text-muted small">No data to display</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-4">
                        <div className="col-md-6">
                            <div className="card border-0 shadow-sm">
                                <div className="card-header bg-white py-3">
                                    <h6 className="mb-0 fw-bold">Recent Quotations ({counts.QUOTATION})</h6>
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0">
                                            <thead className="table-light small">
                                                <tr>
                                                    <th>Number</th>
                                                    <th>Customer</th>
                                                    <th>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentQuotations.map(q => (
                                                    <tr key={q.id} className="small align-middle" onClick={() => handleEdit(q)} style={{ cursor: "pointer" }}>
                                                        <td className="fw-bold text-info">{q.docNumber}</td>
                                                        <td>{q.customerDetails?.name}</td>
                                                        <td className="fw-bold">₹{q.total?.toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                                {recentQuotations.length === 0 && <tr><td colSpan="3" className="text-center py-3 text-muted">No quotes recorded</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="card border-0 shadow-sm p-4 h-100 bg-light">
                                <h6 className="fw-bold border-bottom pb-2 mb-3">Quick Actions</h6>
                                <div className="d-flex flex-wrap gap-2">
                                    <button className="btn btn-sm btn-primary" onClick={() => { setView("INVOICE"); setIsViewOnly(false); setShowModal(true); }}>
                                        <i className="bi bi-plus-circle me-1"></i>New Invoice
                                    </button>
                                    <button className="btn btn-sm btn-warning text-dark" onClick={() => { setView("PROFORMA"); setIsViewOnly(false); setShowModal(true); }}>
                                        <i className="bi bi-file-earmark-text me-1"></i>New Proforma
                                    </button>
                                    <button className="btn btn-sm btn-info text-white" onClick={() => { setView("QUOTATION"); setIsViewOnly(false); setShowModal(true); }}>
                                        <i className="bi bi-file-earmark-plus me-1"></i>New Quotation
                                    </button>
                                    <button className="btn btn-sm btn-outline-success" onClick={() => setView("CUSTOMERS")}>
                                        <i className="bi bi-person-plus me-1"></i>Customers
                                    </button>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setView("ITEMS")}>
                                        <i className="bi bi-box-seam me-1"></i>Item Master
                                    </button>
                                </div>
                                <div className="mt-4 p-3 bg-white border rounded shadow-xs">
                                    <h6 className="x-small fw-bold text-uppercase text-muted mb-2">Billing Intelligence</h6>
                                    <p className="x-small text-muted mb-0">
                                        The <strong>Collection Rate</strong> shows how effectively you are converting invoices into cash.
                                        High outstanding balances in the <strong>Pending Payment</strong> card may require follow-ups.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {["INVOICE", "QUOTATION", "PROFORMA", "CHALAN"].includes(view) && (
                <>
                    {/* Filter Bar */}
                    <div className="card shadow-sm border-0 mb-3">
                        <div className="card-body py-2">
                            <div className="row g-2 align-items-center">
                                <div className="col-md-4">
                                    <div className="input-group input-group-sm">
                                        <span className="input-group-text bg-white">🔍</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={`Search by customer or ${view === "INVOICE" ? "invoice" : (view === "QUOTATION" ? "quotation" : (view === "PROFORMA" ? "proforma" : "DC"))} no...`}
                                            value={searchTerm}
                                            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={startDate}
                                        title="From Date"
                                        onChange={e => { setStartDate(e.target.value); setCurrentPage(1); }}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={endDate}
                                        title="To Date"
                                        onChange={e => { setEndDate(e.target.value); setCurrentPage(1); }}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <select
                                        className="form-select form-select-sm"
                                        value={statusFilter}
                                        onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                    >
                                        <option value="ALL">All Statuses</option>
                                        <option value="DRAFT">Draft</option>
                                        <option value="SENT">Sent</option>
                                        {view === "QUOTATION" ? (
                                            <option value="APPROVED">Approved</option>
                                        ) : view === "CHALAN" ? (
                                            <>
                                                <option value="RETURNED">Returned</option>
                                                <option value="CLOSED">Closed</option>
                                            </>
                                        ) : (
                                            <>
                                                <option value="PAID">Paid</option>
                                                <option value="CANCELLED">Cancelled</option>
                                            </>
                                        )}
                                    </select>
                                </div>
                                <div className="col-md text-end text-muted small">
                                    Total {filteredDocs.length} record(s)
                                </div>
                            </div>
                        </div>
                    </div>
                    {loading ? (
                        <Loader />
                    ) : (
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                                <h5 className="mb-0 text-primary">Recent {view}s</h5>
                                <div className="d-flex gap-2">
                                    {view === "INVOICE" && (
                                        <button className="btn btn-outline-info btn-sm" onClick={handleDownloadGSTJson}>
                                            📁 GST JSON
                                        </button>
                                    )}
                                    <button className="btn btn-outline-success btn-sm" onClick={handleExportExcel}>
                                        <i className="bi bi-file-earmark-excel me-1"></i>Export to Excel
                                    </button>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>{view === "INVOICE" ? "Invoice" : (view === "QUOTATION" ? "Quotation" : (view === "PROFORMA" ? "Proforma" : "DC"))} Number</th>
                                                <th>Date</th>
                                                <th>Customer</th>
                                                {view === "CHALAN" && <th>Type</th>}
                                                <th>Taxable Amt</th>
                                                <th>Tax Type</th>
                                                <th>GST Amount</th>
                                                <th>Total</th>
                                                <th>Status</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedDocs.map(doc => (
                                                <tr key={doc.id}>
                                                    <td className="fw-bold text-primary">{doc.docNumber || "N/A"}</td>
                                                    <td>{doc.date?.toDate().toLocaleDateString()}</td>
                                                    <td>{doc.customerDetails?.name}</td>
                                                    {view === "CHALAN" && (
                                                        <td>
                                                            {(doc.challanType || "NON_RETURNABLE") === "RETURNABLE"
                                                                ? <span className="badge bg-info text-dark">🔄 Returnable</span>
                                                                : <span className="badge bg-secondary">✅ Non-Returnable</span>}
                                                        </td>
                                                    )}
                                                    <td>₹{doc.subtotal?.toFixed(2)}</td>
                                                    <td className="small fw-bold">
                                                        {(getBaseState(doc.customerDetails?.state || "Tamil Nadu (33)") !== getBaseState(profile?.state || "Tamil Nadu (33)")) ? "IGST" : "CGST+SGST"}
                                                    </td>
                                                    <td className="small">
                                                        {(getBaseState(doc.customerDetails?.state || "Tamil Nadu (33)") !== getBaseState(profile?.state || "Tamil Nadu (33)")) ? (
                                                            <span>IGST: ₹{doc.taxAmount?.toFixed(2)}</span>
                                                        ) : (
                                                            <>
                                                                <div>C: ₹{(doc.taxAmount / 2).toFixed(2)}</div>
                                                                <div>S: ₹{(doc.taxAmount / 2).toFixed(2)}</div>
                                                            </>
                                                        )}
                                                    </td>
                                                    <td className="fw-bold">₹{doc.total?.toFixed(2)}</td>
                                                    <td>
                                                        <select
                                                            className={`form-select form-select-sm fw-bold border-0 bg-${
                                                                doc.status === "PAID" || doc.status === "APPROVED" || doc.status === "CONVERTED" || doc.status === "CLOSED"
                                                                    ? "success-subtle text-success"
                                                                    : doc.status === "SENT"
                                                                        ? "primary-subtle text-primary"
                                                                        : doc.status === "CANCELLED" || doc.status === "RETURNED"
                                                                            ? "danger-subtle text-danger"
                                                                            : "warning-subtle text-dark"
                                                            }`}
                                                            style={{ width: "fit-content" }}
                                                            value={doc.status}
                                                            onChange={(e) => handleStatusChange(doc.id, e.target.value)}
                                                        >
                                                            <option value="DRAFT">Draft</option>
                                                            <option value="SENT">Sent</option>
                                                            {view === "QUOTATION" ? (
                                                                <>
                                                                    <option value="APPROVED">Approved</option>
                                                                    <option value="CONVERTED">Converted</option>
                                                                </>
                                                            ) : view === "CHALAN" ? (
                                                                <>
                                                                    {(doc.challanType || "NON_RETURNABLE") === "RETURNABLE" && (
                                                                        <option value="RETURNED">Returned</option>
                                                                    )}
                                                                    <option value="CLOSED">Closed</option>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <option value="PAID">Paid</option>
                                                                    <option value="CANCELLED">Cancelled</option>
                                                                </>
                                                            )}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex justify-content-center gap-2">
                                                            <button className="btn btn-sm btn-outline-info" title="View" onClick={() => handleView(doc)}>👁️</button>
                                                            {view === "QUOTATION" && doc.status !== "CONVERTED" && (
                                                                <>
                                                                    <button className="btn btn-sm btn-outline-warning" title="Convert to Proforma" onClick={() => handleConvertToProforma(doc)}>📄</button>
                                                                    <button className="btn btn-sm btn-outline-success" title="Convert to Invoice" onClick={() => handleConvertToInvoice(doc)}>📜</button>
                                                                </>
                                                            )}
                                                            {view === "PROFORMA" && doc.status !== "CONVERTED" && (
                                                                <button className="btn btn-sm btn-outline-success" title="Convert to Invoice" onClick={() => handleConvertToInvoice(doc)}>📜</button>
                                                            )}
                                                            <button className="btn btn-sm btn-outline-primary" title="Print" onClick={() => handlePrint(doc)}>🖨️</button>
                                                            <button className="btn btn-sm btn-outline-dark" title="Edit" onClick={() => handleEdit(doc)}>✏️</button>
                                                            <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => handleDelete(doc.id)}>🗑️</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            {filteredDocs.length === 0 && (
                                                <tr><td colSpan={view === "CHALAN" ? 10 : 9} className="text-center">No results found</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3 border-top pt-3">
                                        <div className="small text-muted">
                                            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredDocs.length)} of {filteredDocs.length} records
                                        </div>
                                        <div className="btn-group btn-group-sm shadow-sm">
                                            <button
                                                className="btn btn-white border px-3"
                                                disabled={currentPage === 1}
                                                onClick={() => setCurrentPage(p => p - 1)}
                                            >
                                                &laquo; Prev
                                            </button>
                                            {[...Array(totalPages)].map((_, i) => (
                                                <button
                                                    key={i}
                                                    className={`btn ${currentPage === i + 1 ? 'btn-primary' : 'btn-white border'} `}
                                                    onClick={() => setCurrentPage(i + 1)}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}
                                            <button
                                                className="btn btn-white border px-3"
                                                disabled={currentPage === totalPages}
                                                onClick={() => setCurrentPage(p => p + 1)}
                                            >
                                                Next &raquo;
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}

            {showModal && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <form onSubmit={handleSubmit}>
                                <div className="modal-header bg-primary text-white">
                                    <h5 className="modal-title">{isViewOnly ? 'View' : (editingId ? 'Edit' : 'Create')} {view}</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <label className="fw-bold">{view === "INVOICE" ? "Invoice" : (view === "QUOTATION" ? "Quotation" : (view === "PROFORMA" ? "Proforma" : "DC"))} Number</label>
                                            <input
                                                className="form-control"
                                                value={formData.docNumber}
                                                readOnly={isViewOnly}
                                                onChange={e => setFormData({ ...formData, docNumber: e.target.value })}
                                                placeholder="e.g. INV-2024-01"
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="fw-bold">P.O. Number</label>
                                            <input
                                                className="form-control"
                                                value={formData.poNumber}
                                                readOnly={isViewOnly}
                                                onChange={e => setFormData({ ...formData, poNumber: e.target.value })}
                                                placeholder="e.g. PO-12345"
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="fw-bold">Payment Terms</label>
                                            {["Due on receipt", "Net 7 days", "Net 15 Days", "Net 30 Days", "Net 45 Days"].includes(formData.paymentTerms) ? (
                                                <select
                                                    className="form-select"
                                                    value={formData.paymentTerms}
                                                    disabled={isViewOnly}
                                                    onChange={e => {
                                                        if (e.target.value === "CUSTOM") {
                                                            setFormData({ ...formData, paymentTerms: "" }); // Empty for custom input
                                                        } else {
                                                            setFormData({ ...formData, paymentTerms: e.target.value });
                                                        }
                                                    }}
                                                >
                                                    <option value="Due on receipt">Due on receipt</option>
                                                    <option value="Net 7 days">Net 7 days</option>
                                                    <option value="Net 15 Days">Net 15 Days</option>
                                                    <option value="Net 30 Days">Net 30 Days</option>
                                                    <option value="Net 45 Days">Net 45 Days</option>
                                                    <option value="CUSTOM">Custom...</option>
                                                </select>
                                            ) : (
                                                <div className="input-group">
                                                    <input
                                                        className="form-control border-primary"
                                                        value={formData.paymentTerms}
                                                        readOnly={isViewOnly}
                                                        placeholder="Enter custom terms..."
                                                        onChange={e => setFormData({ ...formData, paymentTerms: e.target.value })}
                                                        autoFocus
                                                    />
                                                    <button
                                                        className="btn btn-outline-secondary"
                                                        type="button"
                                                        disabled={isViewOnly}
                                                        onClick={() => setFormData({ ...formData, paymentTerms: "Due on receipt" })}
                                                    >
                                                        ↩
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-3">
                                            <label className="fw-bold">Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={formData.date}
                                                readOnly={isViewOnly}
                                                onChange={e => setFormData({ ...formData, date: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="fw-bold">Select Customer</label>
                                            <select
                                                className="form-select border-primary"
                                                required
                                                disabled={isViewOnly}
                                                value={formData.customerId}
                                                onChange={e => {
                                                    if (e.target.value === "ADD_NEW") {
                                                        setShowQuickCust(true);
                                                    } else {
                                                        setFormData({ ...formData, customerId: e.target.value });
                                                    }
                                                }}
                                            >
                                                <option value="">-- Choose Customer --</option>
                                                {customers.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name} ({c.phone})</option>
                                                ))}
                                                <option value="ADD_NEW" className="fw-bold text-primary">+ Add New Customer</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <h6 className="mb-0">Items / Services</h6>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: "20%" }}>Item & Description</th>
                                                    <th style={{ width: "10%" }}>HSN/SAC</th>
                                                    <th style={{ width: "7%" }}>Qty</th>
                                                    <th style={{ width: "10%" }}>Rate</th>
                                                    <th style={{ width: "12%" }}>Discount</th>
                                                    <th style={{ width: "10%" }}>GST %</th>
                                                    <th style={{ width: "15%" }}>Amount</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            <select
                                                                className="form-select form-select-sm mb-1"
                                                                value={item.itemId}
                                                                disabled={isViewOnly}
                                                                onChange={e => {
                                                                    if (e.target.value === "ADD_NEW") {
                                                                        setActiveItemIndex(idx);
                                                                        setShowQuickItem(true);
                                                                    } else {
                                                                        handleItemChange(idx, "itemId", e.target.value);
                                                                    }
                                                                }}
                                                            >
                                                                <option value="">-- Select --</option>
                                                                {itemsMaster.map(i => (
                                                                    <option key={i.id} value={i.id}>{i.name}</option>
                                                                ))}
                                                                {!isViewOnly && <option value="ADD_NEW" className="fw-bold text-success">+ Add New Item Master</option>}
                                                            </select>
                                                            <textarea
                                                                className="form-control form-control-sm"
                                                                placeholder="Detailed description/note..."
                                                                value={item.itemNote}
                                                                readOnly={isViewOnly}
                                                                onChange={e => handleItemChange(idx, "itemNote", e.target.value)}
                                                                rows="2"
                                                            ></textarea>
                                                        </td>
                                                        <td><input className="form-control form-control-sm" readOnly value={item.hsnSac} /></td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                value={item.qty}
                                                                readOnly={isViewOnly}
                                                                onChange={e => handleItemChange(idx, "qty", Number(e.target.value))}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                value={item.rate}
                                                                readOnly={isViewOnly}
                                                                onChange={e => handleItemChange(idx, "rate", Number(e.target.value))}
                                                            />
                                                        </td>
                                                        <td>
                                                            <div className="input-group input-group-sm">
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    value={item.discount || ""}
                                                                    readOnly={isViewOnly}
                                                                    onChange={e => handleItemChange(idx, "discount", e.target.value)}
                                                                />
                                                                <select
                                                                    className="form-select border-start-0"
                                                                    style={{ maxWidth: "50px", padding: "0 5px" }}
                                                                    value={item.discountType || "₹"}
                                                                    disabled={isViewOnly}
                                                                    onChange={e => handleItemChange(idx, "discountType", e.target.value)}
                                                                >
                                                                    <option value="₹">₹</option>
                                                                    <option value="%">%</option>
                                                                </select>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <select
                                                                className="form-select form-select-sm"
                                                                value={hasGstInUI ? item.taxRate : 0}
                                                                disabled={isViewOnly || !hasGstInUI}
                                                                onChange={e => handleItemChange(idx, "taxRate", Number(e.target.value))}
                                                            >
                                                                <option value="0">0%</option>
                                                                <option value="5">5%</option>
                                                                <option value="12">12%</option>
                                                                <option value="18">18%</option>
                                                                <option value="28">28%</option>
                                                            </select>
                                                        </td>
                                                        <td><input className="form-control form-control-sm text-end" readOnly value={hasGstInUI && item.taxAmount > 0 ? `${item.amount.toFixed(2)} (+${item.taxAmount.toFixed(2)})` : item.amount.toFixed(2)} /></td>
                                                        <td className="text-center">
                                                            {!isViewOnly && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveItem(idx)}>×</button>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {!isViewOnly && <button type="button" className="btn btn-sm btn-success" onClick={handleAddItem}>+ Add Item</button>}
                                    </div>

                                    {/* Challan Type - only for CHALAN view */}
                                    {view === "CHALAN" && (
                                        <div className="row mt-3">
                                            <div className="col-12">
                                                <label className="fw-bold mb-2">Challan Type</label>
                                                <div className="d-flex gap-3">
                                                    <div
                                                        className={`card px-4 py-3 flex-row align-items-center gap-2 cursor-pointer border-2 ${formData.challanType === "NON_RETURNABLE" ? "border-secondary bg-secondary bg-opacity-10" : "border-light"}`}
                                                        style={{ cursor: isViewOnly ? "default" : "pointer", minWidth: 180 }}
                                                        onClick={() => !isViewOnly && setFormData({ ...formData, challanType: "NON_RETURNABLE", returnRemarks: "" })}
                                                    >
                                                        <input
                                                            type="radio"
                                                            id="challanNonReturnable"
                                                            name="challanType"
                                                            value="NON_RETURNABLE"
                                                            disabled={isViewOnly}
                                                            checked={formData.challanType === "NON_RETURNABLE"}
                                                            onChange={() => setFormData({ ...formData, challanType: "NON_RETURNABLE", returnRemarks: "" })}
                                                        />
                                                        <label htmlFor="challanNonReturnable" className="mb-0 fw-bold" style={{ cursor: "pointer" }}>
                                                            ✅ Non-Returnable
                                                        </label>
                                                    </div>
                                                    <div
                                                        className={`card px-4 py-3 flex-row align-items-center gap-2 border-2 ${formData.challanType === "RETURNABLE" ? "border-info bg-info bg-opacity-10" : "border-light"}`}
                                                        style={{ cursor: isViewOnly ? "default" : "pointer", minWidth: 180 }}
                                                        onClick={() => !isViewOnly && setFormData({ ...formData, challanType: "RETURNABLE" })}
                                                    >
                                                        <input
                                                            type="radio"
                                                            id="challanReturnable"
                                                            name="challanType"
                                                            value="RETURNABLE"
                                                            disabled={isViewOnly}
                                                            checked={formData.challanType === "RETURNABLE"}
                                                            onChange={() => setFormData({ ...formData, challanType: "RETURNABLE" })}
                                                        />
                                                        <label htmlFor="challanReturnable" className="mb-0 fw-bold text-info" style={{ cursor: "pointer" }}>
                                                            🔄 Returnable
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                            {formData.challanType === "RETURNABLE" && (
                                                <div className="col-md-7 mt-3">
                                                    <label className="fw-bold text-info">Return Remarks <span className="text-muted small fw-normal">(expected return info)</span></label>
                                                    <textarea
                                                        className="form-control border-info"
                                                        rows="2"
                                                        placeholder="e.g. Goods to be returned within 7 days from dispatch date."
                                                        value={formData.returnRemarks}
                                                        readOnly={isViewOnly}
                                                        onChange={e => setFormData({ ...formData, returnRemarks: e.target.value })}
                                                    ></textarea>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="row mt-3">
                                        <div className="col-md-7">
                                            <label>Remarks</label>
                                            <textarea
                                                className="form-control"
                                                rows="4"
                                                value={formData.remarks}
                                                readOnly={isViewOnly}
                                                onChange={e => setFormData({ ...formData, remarks: e.target.value })}
                                            ></textarea>
                                        </div>
                                        <div className="col-md-5">
                                            <div className="card bg-light p-3">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span>Subtotal</span>
                                                    <span>₹{subtotal.toFixed(2)}</span>
                                                </div>
                                                {isCurrentInterState ? (
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <span>IGST</span>
                                                        <span>₹{taxAmount.toFixed(2)}</span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <span className="small">CGST</span>
                                                            <span className="small">₹{(taxAmount / 2).toFixed(2)}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="small">SGST</span>
                                                            <span className="small">₹{(taxAmount / 2).toFixed(2)}</span>
                                                        </div>
                                                    </>
                                                )}
                                                <hr />
                                                <div className="d-flex justify-content-between fw-bold fs-5">
                                                    <span>Grand Total</span>
                                                    <span>₹{total.toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>{isViewOnly ? 'Close' : 'Cancel'}</button>
                                    {!isViewOnly && (
                                        <button type="submit" className="btn btn-primary" disabled={loading}>
                                            {loading ? <span className="spinner-border spinner-border-sm" /> : `Save ${view} `}
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* QUICK ADD CUSTOMER MODAL */}
            {showQuickCust && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.6)", zIndex: 1100 }}>
                    <div className="modal-dialog">
                        <div className="modal-content shadow-lg">
                            <form onSubmit={handleQuickCustSave}>
                                <div className="modal-header bg-primary text-white py-2">
                                    <h6 className="modal-title">Add Full Customer Details</h6>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowQuickCust(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="row g-2">
                                        <div className="col-md-6 mb-2">
                                            <label className="small fw-bold">Name</label>
                                            <input className="form-control form-control-sm" required value={quickCust.name} onChange={e => setQuickCust({ ...quickCust, name: e.target.value })} />
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label className="small fw-bold">Phone</label>
                                            <input className="form-control form-control-sm" required value={quickCust.phone} onChange={e => setQuickCust({ ...quickCust, phone: e.target.value })} />
                                        </div>
                                        <div className="col-12 mb-2">
                                            <label className="small fw-bold">Billing State</label>
                                            <select
                                                className="form-select form-select-sm"
                                                value={quickCust.state}
                                                onChange={e => {
                                                    const newState = e.target.value;
                                                    setQuickCust(prev => ({
                                                        ...prev,
                                                        state: newState,
                                                        shippingState: prev.sameAsBilling ? newState : prev.shippingState
                                                    }));
                                                }}
                                            >
                                                {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label className="small">GSTIN (Optional)</label>
                                            <input className="form-control form-control-sm" value={quickCust.gstin} onChange={e => setQuickCust({ ...quickCust, gstin: e.target.value })} />
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label className="small">Email (Optional)</label>
                                            <input type="email" className="form-control form-control-sm" value={quickCust.email} onChange={e => setQuickCust({ ...quickCust, email: e.target.value })} />
                                        </div>
                                        <div className="col-12 mb-2">
                                            <label className="small fw-bold">Billing Address</label>
                                            <textarea
                                                className="form-control form-control-sm"
                                                rows="2"
                                                value={quickCust.address}
                                                onChange={e => {
                                                    const newAddr = e.target.value;
                                                    setQuickCust(prev => ({
                                                        ...prev,
                                                        address: newAddr,
                                                        shippingAddress: prev.sameAsBilling ? newAddr : prev.shippingAddress
                                                    }));
                                                }}
                                            ></textarea>
                                        </div>
                                        <div className="col-12 mb-2">
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="quickSameAsBilling"
                                                    checked={quickCust.sameAsBilling}
                                                    onChange={e => {
                                                        const checked = e.target.checked;
                                                        setQuickCust(prev => ({
                                                            ...prev,
                                                            sameAsBilling: checked,
                                                            shippingAddress: checked ? prev.address : prev.shippingAddress,
                                                            shippingState: checked ? prev.state : prev.shippingState
                                                        }));
                                                    }}
                                                />
                                                <label className="form-check-label small fw-bold" htmlFor="quickSameAsBilling">
                                                    Shipping same as billing
                                                </label>
                                            </div>
                                        </div>
                                        {!quickCust.sameAsBilling && (
                                            <>
                                                <div className="col-12 mb-2">
                                                    <label className="small fw-bold">Shipping Address</label>
                                                    <textarea
                                                        className="form-control form-control-sm"
                                                        rows="2"
                                                        value={quickCust.shippingAddress}
                                                        onChange={e => setQuickCust({ ...quickCust, shippingAddress: e.target.value })}
                                                    ></textarea>
                                                </div>
                                                <div className="col-12 mb-2">
                                                    <label className="small fw-bold">Shipping State</label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={quickCust.shippingState}
                                                        onChange={e => setQuickCust({ ...quickCust, shippingState: e.target.value })}
                                                    >
                                                        {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer py-2">
                                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowQuickCust(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-sm btn-primary" disabled={loading}>
                                        {loading ? <span className="spinner-border spinner-border-sm" /> : "Save & Select"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* QUICK ADD ITEM MODAL */}
            {showQuickItem && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.6)", zIndex: 1100 }}>
                    <div className="modal-dialog">
                        <div className="modal-content shadow-lg">
                            <form onSubmit={handleQuickItemSave}>
                                <div className="modal-header bg-success text-white py-2">
                                    <h6 className="modal-title">Add Full Item Details</h6>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowQuickItem(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="row g-2">
                                        <div className="col-12 mb-2">
                                            <label className="small fw-bold">Item Name</label>
                                            <input className="form-control form-control-sm" required value={quickItem.name} onChange={e => setQuickItem({ ...quickItem, name: e.target.value })} />
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label className="small fw-bold">Type</label>
                                            <select
                                                className="form-select form-select-sm border-primary fw-bold"
                                                value={quickItem.type}
                                                onChange={e => setQuickItem({ ...quickItem, type: e.target.value })}
                                            >
                                                <option value="Goods">📦 Goods</option>
                                                <option value="Services">🛠 Services</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label className="small fw-bold">HSN/SAC Code</label>
                                            <input className="form-control form-control-sm" required value={quickItem.hsnSac} onChange={e => setQuickItem({ ...quickItem, hsnSac: e.target.value })} />
                                        </div>

                                        {quickItem.type === "Goods" && (
                                            <>
                                                <div className="col-md-6 mb-2">
                                                    <label className="small fw-bold">Part Number</label>
                                                    <input className="form-control form-control-sm" value={quickItem.partNumber} onChange={e => setQuickItem({ ...quickItem, partNumber: e.target.value })} />
                                                </div>
                                                <div className="col-md-6 mb-2">
                                                    <label className="small fw-bold">Purchase Price</label>
                                                    <input type="number" className="form-control form-control-sm" value={quickItem.purchasePrice} onChange={e => setQuickItem({ ...quickItem, purchasePrice: e.target.value })} />
                                                </div>
                                                <div className="col-md-6 mb-2">
                                                    <label className="small fw-bold">Selling Price</label>
                                                    <input type="number" className="form-control form-control-sm border-primary" value={quickItem.sellingPrice} onChange={e => setQuickItem({ ...quickItem, sellingPrice: e.target.value })} />
                                                </div>
                                            </>
                                        )}

                                        {quickItem.type === "Services" && (
                                            <div className="col-md-6 mb-2">
                                                <label className="small fw-bold">Service Rate</label>
                                                <input type="number" className="form-control form-control-sm border-primary" value={quickItem.sellingPrice || quickItem.defaultRate} onChange={e => setQuickItem({ ...quickItem, sellingPrice: e.target.value, defaultRate: e.target.value })} />
                                            </div>
                                        )}
                                        <div className="col-md-4 mb-2">
                                            <label className="small">Default Rate</label>
                                            <input type="number" className="form-control form-control-sm" value={quickItem.defaultRate} onChange={e => setQuickItem({ ...quickItem, defaultRate: e.target.value })} />
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <label className="small">GST %</label>
                                            <select
                                                className="form-select form-select-sm"
                                                value={quickItem.taxRate}
                                                onChange={e => setQuickItem({ ...quickItem, taxRate: Number(e.target.value) })}
                                            >
                                                <option value="0">0%</option>
                                                <option value="5">5%</option>
                                                <option value="12">12%</option>
                                                <option value="18">18%</option>
                                                <option value="28">28%</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <label className="small">Unit</label>
                                            <select className="form-select form-select-sm" value={quickItem.unit} onChange={e => setQuickItem({ ...quickItem, unit: e.target.value })}>
                                                <option value="Nos">Nos</option>
                                                <option value="Sets">Sets</option>
                                                <option value="Hrs">Hrs</option>
                                                <option value="Ltr">Ltr</option>
                                                <option value="Kg">Kg</option>
                                                <option value="Mtr">Mtr</option>
                                            </select>
                                        </div>
                                        <div className="col-12 mb-2">
                                            <label className="small">Description (Optional)</label>
                                            <textarea className="form-control form-control-sm" rows="3" value={quickItem.description} onChange={e => setQuickItem({ ...quickItem, description: e.target.value })}></textarea>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer py-2">
                                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowQuickItem(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-sm btn-success" disabled={loading}>
                                        {loading ? <span className="spinner-border spinner-border-sm" /> : "Save Master & Apply"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
